import { useCallback } from 'react';
import { useRouter } from 'expo-router';
import { dismissNotification, dismissNotificationsByIds, markNotificationRead } from '../service';
import type { Notification } from '../types';
import { getDocumentsTabTarget, getProfileTabTarget } from '@/navigation/tabTargets';

export type NotificationActionContext = 'trips' | 'plan';

export type NotificationActionCallbacks = {
  /**
   * Called when the notification modal should close
   */
  onClose?: () => void;
  /**
   * Called for missing_transport/missing_lodging notifications in plan context
   */
  onOpenAddItem?: () => void;
  /**
   * Called for budget notifications in plan context
   */
  onLaunchBudget?: () => void;
  /**
   * Called for collaborators notifications
   * @param tripId - The trip ID to open collaboration for
   */
  onOpenCollaboration?: (tripId?: string) => void;
  /**
   * Called when a visa search should be opened
   * @param citizenship - User's citizenship
   * @param destination - Trip destination
   */
  onVisaSearch?: (citizenship: string, destination: string) => void;
  /**
   * Called when the offline download/settings screen should open
   */
  onOpenOfflineDownload?: () => void;
  /**
   * Called after handling an action for analytics/tracking
   */
  onActionTracked?: (notification: Notification, action: string) => void;
  /**
   * Toast/feedback function for errors
   */
  showInfo?: (message: string) => void;
};

export type NotificationActionConfig = {
  /**
   * Context determines default behaviors
   * - 'trips': Navigate to plan (or wallet for budget) for most actions
   * - 'plan': Perform in-place actions
   */
  context: NotificationActionContext;
  /**
   * Current trip ID (required for plan context)
   */
  tripId?: string;
  /**
   * User's citizenship for visa searches
   */
  citizenship?: string;
  /**
   * Trip destination for visa searches
   */
  destination?: string;
  /**
   * Callbacks for specific actions
   */
  callbacks?: NotificationActionCallbacks;
};

/**
 * Shared hook for handling notification actions consistently across screens.
 * 
 * In 'trips' context: navigates to plan for most actions; budget opens Wallet
 * In 'plan' context: performs actions in-place (opens modals, etc.)
 */
export function useNotificationActionHandler(config: NotificationActionConfig) {
  const router = useRouter();
  const { context, tripId, citizenship, destination, callbacks } = config;

  /**
   * Handle a notification action (CTA button pressed)
   */
  const handleAction = useCallback(
    (notification: Notification) => {
      const isChecklistAlert =
        notification.kind === 'missing_transport' ||
        notification.kind === 'missing_lodging' ||
        notification.kind === 'visa' ||
        notification.kind === 'offline' ||
        notification.kind === 'collaborators' ||
        notification.kind === 'docs' ||
        notification.kind === 'check_in';
      // For checklist-style alerts, opening the CTA should not mark the item "done".
      // Users should explicitly dismiss/toggle when they've actually completed it.
      if (!isChecklistAlert) {
        markNotificationRead(notification.id);
      }
      
      const notificationTripId = notification.scopeId || (notification.meta?.tripId as string) || tripId;

      // Track the action if callback is provided
      callbacks?.onActionTracked?.(notification, 'action_taken');

      // Close the modal
      callbacks?.onClose?.();

      // Handle based on notification kind
      switch (notification.kind) {
        case 'missing_transport':
        case 'missing_lodging':
          if (context === 'plan' && callbacks?.onOpenAddItem) {
            callbacks.onOpenAddItem();
          } else if (notificationTripId) {
            router.push({
              pathname: '/(tabs)/plan',
              params: { tripId: notificationTripId },
            });
          }
          break;

        case 'budget':
          if (context === 'plan' && callbacks?.onLaunchBudget) {
            callbacks.onLaunchBudget();
          } else if (notificationTripId) {
            router.push({
              pathname: '/(tabs)/wallet',
              params: { tripId: notificationTripId },
            });
          }
          break;

        case 'docs':
          router.push(getDocumentsTabTarget('merged'));
          break;

        case 'collaborators':
          if (callbacks?.onOpenCollaboration) {
            callbacks.onOpenCollaboration(notificationTripId);
          } else if (notificationTripId) {
            router.push({
              pathname: '/(tabs)/plan',
              params: { tripId: notificationTripId },
            });
          }
          break;

        case 'visa':
          if (callbacks?.onVisaSearch) {
            callbacks.onVisaSearch(citizenship || 'your country', destination || 'destination');
          } else {
            // No Google fallback. Prefer in-app navigation.
            if (notificationTripId) {
              router.push({
                pathname: '/(tabs)/plan',
                params: { tripId: notificationTripId },
              });
            } else {
              callbacks?.showInfo?.(
                'Open the trip to view the Visa Requirements checklist, or check the destination country’s official immigration website.',
              );
            }
          }
          break;

        case 'offline':
          if (callbacks?.onOpenOfflineDownload) {
            callbacks.onOpenOfflineDownload();
          } else {
            // Fallback: open Settings/Profile tab (where offline mode lives)
            router.push(getProfileTabTarget('merged'));
          }
          break;

        case 'check_in':
          if (notificationTripId) {
            router.push({
              pathname: '/(tabs)/plan',
              params: { tripId: notificationTripId },
            });
          }
          break;

        default:
          // For unknown kinds, navigate to plan if we have a tripId
          if (context === 'plan' && callbacks?.onOpenAddItem) {
            callbacks.onOpenAddItem();
          } else if (notificationTripId) {
            router.push({
              pathname: '/(tabs)/plan',
              params: { tripId: notificationTripId },
            });
          }
          break;
      }
    },
    [context, tripId, citizenship, destination, callbacks, router],
  );

  /**
   * Handle dismissing a notification.
   * All notification kinds are permanently dismissed.
   */
  const handleDismiss = useCallback(
    async (notification: Notification) => {
      await dismissNotification(notification.id);
      callbacks?.onActionTracked?.(notification, 'dismissed');
    },
    [callbacks],
  );

  /**
   * Handle dismissing all notifications.
   * All notification kinds are permanently dismissed.
   */
  const handleDismissAll = useCallback(
    async (notifications: Notification[]) => {
      const ids = notifications.map((n) => n.id);
      if (!ids.length) return;
      await dismissNotificationsByIds(ids);
    },
    [],
  );

  return {
    handleAction,
    handleDismiss,
    handleDismissAll,
  };
}

